import {
  Button,
  Group,
  InputWrapper,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useField, UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import {
  IconArrowRight,
  IconListSearch,
  IconLock,
  IconLockOpen2,
  IconPencil,
  IconReport,
} from "@tabler/icons-react"
import _ from "lodash"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { TreeNodeType } from "@costory/types/endpoints/folders"
import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"
import { Filters } from "@costory/types/filters"
import { ComponentType } from "@costory/types/prisma-client"

import { FileExplorer } from "@costory/front/components/FileExplorer/FileExplorer"
import { ScheduledDigestConfigurationsTable } from "@costory/front/components/ScheduledDigestConfigurationsTable"
import { ModalCurrency } from "@costory/front/components/charts/ModalCurrency"
import useCmdClick from "@costory/front/components/common/CmdClick"
import { navigateWithCmd } from "@costory/front/components/common/NavigateWithCmd"
import { UnifiedActionBar } from "@costory/front/components/common/UnifiedActionBar"
import { useUnsavedChangesBlocker } from "@costory/front/components/common/useUnsavedChangesBlocker"
import { runtimeConfig } from "@costory/front/lib/runtimeConfig"
import { ConfigurationModal } from "@costory/front/pages/ScheduledDigestConfigurations/ConfigurationModal"
import { useAuthState } from "@costory/front/queries/auth"
import {
  SavedViewRedirectPage,
  useCreateSavedViewMutation,
  useUpdateSavedViewMutation,
} from "@costory/front/queries/savedViews"

type Props = {
  currentView?: SavedViewsResponses.GetSavedViewDetails
  redirectPage: SavedViewRedirectPage
  form: UseFormReturnType<Filters>
  referenceFilters?: Filters
  urlFilters?: Filters
}

export const SavedViewsSelector = ({
  currentView,
  redirectPage,
  form,
  urlFilters,
}: Props) => {
  const isExistingView = Boolean(currentView)
  const auth = useAuthState()

  const navigate = useNavigate()
  const viewNameField = useField({ initialValue: currentView?.name ?? "" })
  const newViewNameField = useField({
    initialValue: currentView ? currentView.name + " (copy)" : "",
  })
  const [viewType, setViewType] = useState<ComponentType>(
    currentView?.type ?? ComponentType.PUBLIC,
  )
  const [parentFolderId, setParentFolderId] = useState<string | null>(
    currentView?.parentFolderId || null,
  )
  const [isTouched, { open: setIsTouched, close: resetIsTouched }] =
    useDisclosure()
  const [isOpen, { close: closeSaveViewModal, open: openSaveViewModal }] =
    useDisclosure(false, {
      onOpen: () => setParentFolderId(currentView?.parentFolderId || null),
    })

  const { mutateAsync: createSavedView, isPending: isCreatingSavedView } =
    useCreateSavedViewMutation(redirectPage)
  const { mutateAsync: updateSavedView, isPending: isUpdatingSavedView } =
    useUpdateSavedViewMutation(currentView?.id)
  const [openedCurrencyModal, currencyModalHandlers] = useDisclosure()

  const COSTORY_ORGID = runtimeConfig.VITE_COSTORY_ORGID
  const COSTORY_EMAIL_DOMAIN = runtimeConfig.VITE_COSTORY_EMAIL_DOMAIN
  const isAllowedToEdit =
    (currentView &&
      currentView.createdById == auth.user?.id &&
      currentView?.type !== ComponentType.COMMUNITY) ||
    (auth.user?.isAdmin &&
      ((currentView?.type == ComponentType.COMMUNITY &&
        auth.user?.currentOrg.id === COSTORY_ORGID &&
        auth.user?.email.endsWith(COSTORY_EMAIL_DOMAIN)) ||
        currentView?.type == ComponentType.PUBLIC))

  useUnsavedChangesBlocker({
    isDirtyCallback: () => isTouched && isExistingView,
  })

  const isFormTouched = !!urlFilters

  useEffect(() => {
    if (
      isFormTouched ||
      (isExistingView &&
        (viewNameField.getValue() !== currentView!.name ||
          viewType !== currentView!.type))
    ) {
      setIsTouched()
    } else {
      resetIsTouched()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewNameField, viewType])

  const handleClickReset = () => {
    const path = currentView ? `/explorer/views/${currentView.id}` : "/explorer"
    navigate(path)
  }

  const handleOpenFolder = async (folderId: string) => {
    const filtersWithoutEvents = _.omit(form.getValues(), "events")
    closeSaveViewModal()
    await createSavedView({
      name: newViewNameField.getValue(),
      type: viewType,
      parentFolderId: folderId,
      ...filtersWithoutEvents,
    })
  }

  const handleUpdateView = async () => {
    try {
      await updateSavedView({
        name: viewNameField.getValue(),
        type: viewType,
        parentFolderId,
        ...form.getValues(),
      })
      closeSaveViewModal()
      closeEditTitleModal()
      navigate(`/explorer/views/${currentView?.id}`)
    } catch (e) {
      console.error(e)
    }
  }

  const [
    openedSelectViewModal,
    { close: closeSelectViewModal, open: openSelectViewModal },
  ] = useDisclosure()

  const [
    openedScheduledDigestModal,
    { close: closeScheduledDigestModal, open: openScheduledDigestModal },
  ] = useDisclosure()

  const [
    openedScheduledDigestListModal,
    {
      close: closeScheduledDigestListModal,
      open: openScheduledDigestListModal,
    },
  ] = useDisclosure()

  const [
    openedEditTitleModal,
    { open: openEditTitleModal, close: closeEditTitleModal },
  ] = useDisclosure(false)

  const toggleViewType = (isAdmin?: boolean) => {
    setViewType((prev) =>
      prev === ComponentType.PUBLIC
        ? ComponentType.PRIVATE
        : prev === ComponentType.PRIVATE
          ? isAdmin && auth.user?.currentOrg.id === COSTORY_ORGID
            ? ComponentType.COMMUNITY
            : ComponentType.PUBLIC
          : ComponentType.PUBLIC,
    )
  }

  const isModifierPressed = useCmdClick()
  const handleOpenSavedView = (id: string) => {
    if (id === currentView?.id) {
      closeSelectViewModal()
    }
    navigateWithCmd("/explorer/views/" + id, isModifierPressed, navigate)
  }

  useEffect(() => {
    if (isOpen) closeSaveViewModal()
    if (openedCurrencyModal) currencyModalHandlers.close()
    if (openedSelectViewModal) closeSelectViewModal()
    if (openedScheduledDigestModal) closeScheduledDigestModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters])

  const canHaveScheduledDigests =
    currentView &&
    currentView.type !== ComponentType.PRIVATE &&
    currentView.datePreset !== null

  return (
    <>
      <ModalCurrency
        opened={openedCurrencyModal}
        close={currencyModalHandlers.close}
      />
      <Modal
        size="80vw"
        opened={openedSelectViewModal}
        onClose={closeSelectViewModal}
        title={<Title>Select an existing view</Title>}
        styles={{ body: { height: "70vh" } }}
      >
        <FileExplorer
          onDoubleClick={(viewId, type) => {
            if (type !== TreeNodeType.SavedView) {
              return
            }
            handleOpenSavedView(viewId)
          }}
          defaultShouldShowDashboards={false}
          onOpenSavedView={handleOpenSavedView}
          isInSaveMode={false}
        />
      </Modal>
      {currentView && (
        <ConfigurationModal
          opened={openedScheduledDigestModal}
          onClose={closeScheduledDigestModal}
          initialValues={{
            savedViewId: currentView.id,
          }}
        />
      )}
      <Modal
        size="90vw"
        opened={openedScheduledDigestListModal}
        onClose={closeScheduledDigestListModal}
        title={<Title>Related scheduled reports</Title>}
      >
        <Stack>
          <ScheduledDigestConfigurationsTable />
          <Group justify="end">
            <Button
              rightSection={<IconArrowRight />}
              variant="transparent"
              onClick={() => {
                navigate("/scheduled-reports")
              }}
              data-intercom-target="costory-tour-explorer-go-to-reports-page"
            >
              Go to scheduled reports page
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Stack>
        <UnifiedActionBar
          isExisting={isExistingView}
          onOpen={openSelectViewModal}
          onDuplicate={openSaveViewModal}
          settingsItems={
            isExistingView && isAllowedToEdit
              ? [
                  {
                    label: "Rename",
                    icon: <IconPencil />,
                    onClick: openEditTitleModal,
                  },
                  {
                    label:
                      viewType === ComponentType.PUBLIC
                        ? "Make private"
                        : viewType === ComponentType.COMMUNITY
                          ? "Make public"
                          : COSTORY_ORGID === auth.user?.currentOrg.id &&
                              auth.user?.isAdmin
                            ? "Make community"
                            : "Make public",
                    icon:
                      viewType === ComponentType.PUBLIC ? (
                        <IconLockOpen2 />
                      ) : (
                        <IconLock />
                      ),
                    color:
                      viewType === ComponentType.COMMUNITY
                        ? "success"
                        : viewType === ComponentType.PRIVATE
                          ? "textMuted"
                          : undefined,
                    onClick: () => toggleViewType(auth.user?.isAdmin ?? false),
                    disabled:
                      viewType === ComponentType.PUBLIC &&
                      currentView?.createdById !== auth.user?.id,
                  },
                ]
              : []
          }
          exportItems={
            canHaveScheduledDigests
              ? [
                  {
                    label:
                      currentView.scheduledDigestConfigurations.length > 0
                        ? "Manage Reports"
                        : "Schedule Report",
                    icon:
                      currentView.scheduledDigestConfigurations.length > 0 ? (
                        <IconListSearch />
                      ) : (
                        <IconReport />
                      ),
                    onClick:
                      currentView.scheduledDigestConfigurations.length > 0
                        ? openScheduledDigestListModal
                        : openScheduledDigestModal,
                  },
                ]
              : []
          }
          showDiscardButton={isTouched && isExistingView && isAllowedToEdit}
          onDiscard={handleClickReset}
          showSaveButton={
            (isTouched && isExistingView && isAllowedToEdit) || !isExistingView
          }
          onSave={isExistingView ? handleUpdateView : openSaveViewModal}
          saveButtonLabel={isExistingView ? "Save Changes" : "Save View"}
          saveButtonLoading={isCreatingSavedView || isUpdatingSavedView}
        />
      </Stack>
      <Modal
        size="80vw"
        opened={isOpen}
        onClose={closeSaveViewModal}
        title={<Title order={2}>Save view</Title>}
        styles={{ body: { height: "70vh" } }}
      >
        <Stack align="stretch" h="90%">
          <InputWrapper>
            <Text fw="bold">1. Choose a view name</Text>
            <TextInput
              {...newViewNameField.getInputProps()}
              flex={1}
              placeholder="Production cost breakdown"
              data-intercom-target="costory-tour-explorer-new-view-name-input"
            />
          </InputWrapper>
          <Text fw="bold">2. Select a folder</Text>
          <Stack h="90%">
            <FileExplorer
              onOpenFolder={handleOpenFolder}
              buttonProps={{
                label: "Save",
                loading: isCreatingSavedView,
                disabled: !newViewNameField.getValue().trim(),
                disabledTooltip: "Give your view a name to save it",
              }}
              onDoubleClick={(id, type) => {
                if (type !== TreeNodeType.Folder) {
                  return
                }

                handleOpenFolder(id)
              }}
              disableHighlightStyle={true}
              isInSaveMode
            />
          </Stack>
        </Stack>
      </Modal>
      <Modal
        opened={openedEditTitleModal}
        onClose={() => {
          closeEditTitleModal()
          viewNameField.reset()
        }}
        title={<Title>Edit View Title</Title>}
      >
        <TextInput
          {...viewNameField.getInputProps()}
          placeholder="Unnamed view"
          data-intercom-target="costory-tour-explorer-view-name-input"
        />
        <Group justify="end" mt="md">
          <Button
            variant="filled"
            onClick={handleUpdateView}
            disabled={!viewNameField.getValue().trim()}
            data-intercom-target="costory-tour-explorer-save-view-btn"
          >
            Save
          </Button>
        </Group>
      </Modal>
    </>
  )
}
